import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { IconComponent } from '@copado/cds';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { merge } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';

import { UserState } from '@app/auth/user.state';
import { OrgSwitcherListComponent } from '@app/main-nav/org-switcher-list/org-switcher-list.component';
import { AppModal } from '@app/shared/app-modal.service';
import { filterNullish } from '@app/shared/rxjs-utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  selector: 'app-org-switcher',
  styleUrls: ['./org-switcher.component.scss'],
  templateUrl: './org-switcher.component.html',
})
export class OrgSwitcherComponent {
  private showOrgSwitcher = false;

  private readonly modalService = inject(AppModal);
  private readonly userState = inject(UserState);

  /**
   * The Org Switcher is used in multiple places. By default we list all the orgs that the user
   * has a role in.
   *
   * But when the user is logged in with an incorrect login provider, we only want to list the orgs
   * that have the current login provider in the allowedLoginProviders.
   */
  public readonly showOnlyAccessibleOrgs = input<boolean>(false);
  public readonly closeIncorrectLoginProviderModal = output<void>();

  constructor() {
    toObservable(this.userState.user)
      .pipe(
        filterNullish(),
        distinctUntilChanged((a, b) => a.id !== b.id),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.launchOrgListModel());
  }

  private launchOrgListModel(): void {
    const currentUser = this.userState.user();
    const orgs = this.userState.orgs();

    if (currentUser && orgs.length > 1) {
      if (
        /**
         * If the default org hasn't been set, we auto-launch the org switcher and force the
         * user to set it.
         */
        !currentUser.defaultOrgId ||
        /**
         * Another scenario is that the user has set an org as their default, but they don't have
         * a role in that org anymore.
         */
        !orgs.find((o) => o.id === currentUser.defaultOrgId)
      ) {
        this.launchOrgSwitcherModal({
          backdrop: 'static',
          keyboard: false,
        });
      }
    }
  }

  @HostListener('click')
  public launchOrgSwitcherModal(options: NgbModalOptions = {}): void {
    if (this.userState.user() && !this.showOrgSwitcher) {
      this.showOrgSwitcher = true;
      const modalRef = this.modalService.open(OrgSwitcherListComponent, options);
      const componentInstance: OrgSwitcherListComponent = modalRef.componentInstance;
      componentInstance.showOnlyAccessibleOrgs.set(this.showOnlyAccessibleOrgs());
      merge([modalRef.closed, modalRef.dismiss])
        .pipe(take(1))
        .subscribe(() => {
          this.showOrgSwitcher = false;
          this.closeIncorrectLoginProviderModal.emit();
        });
    }
  }
}
